/**
 * CC-073 / CC-075 / CC-077 — pure UI-state helper for the unified
 * "Забрать" (claim-all) button on the Royalties page.
 *
 * One button per token settles BOTH fee streams (custody TON + LP-fee
 * jettons). CC-077 added a minimum-claim threshold on the TON side: below
 * it the button is disabled with a "накопи ещё" hint. The jetton side has
 * no threshold, so jetton-only accrual still makes the button claimable.
 *
 * Kept pure (no React) so the enable/disable logic is unit-testable in
 * isolation — same pattern as lib/displayMath.ts.
 */

export type ClaimAllState =
  /** Nothing accrued anywhere — no button shown. */
  | { kind: 'none' }
  /**
   * CC-077 — TON accrued but under the claim threshold, and nothing else
   * to claim. Button disabled, "накопи ещё" hint.
   */
  | { kind: 'below_threshold' }
  /** Something is claimable but no wallet connected — prompt to connect. */
  | { kind: 'connect' }
  /** Claimable + wallet connected — button enabled. */
  | { kind: 'claimable' }
  /** A claim for this token is in flight — button disabled, spinner. */
  | { kind: 'busy' };

/**
 * Decide the claim-all button state for one royalty row.
 *
 *   TON ≥ threshold (and > 0)  → TON side is claimable
 *   jetton > 0                 → jetton side is claimable (no threshold)
 *
 *   neither claimable, TON > 0 → 'below_threshold' (sub-threshold dust)
 *   neither claimable, TON = 0 → 'none'
 *   claimable + busy           → 'busy'
 *   claimable + !connected     → 'connect'
 *   claimable + connected      → 'claimable'
 */
export function claimAllState(args: {
  tonAmount: bigint;
  jettonAmount: bigint;
  thresholdNano: bigint;
  walletConnected: boolean;
  busy: boolean;
}): ClaimAllState {
  const tonEligible =
    args.tonAmount > 0n && args.tonAmount >= args.thresholdNano;
  const jettonEligible = args.jettonAmount > 0n;

  if (!tonEligible && !jettonEligible) {
    // Sub-threshold TON dust with nothing else → show the "накопи ещё" hint.
    return args.tonAmount > 0n
      ? { kind: 'below_threshold' }
      : { kind: 'none' };
  }
  if (args.busy) return { kind: 'busy' };
  if (!args.walletConnected) return { kind: 'connect' };
  return { kind: 'claimable' };
}
